/**
 * VSA Scanner Dashboard
 * Style: Consistent with SilentAccumulation page
 */
import { useState, useEffect, useCallback } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { vsaAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import TickerDetailDrawer from '../components/vsa/TickerDetailDrawer';
import OverviewSection from '../components/vsa/OverviewSection';
import RecommendationsSection from '../components/vsa/RecommendationsSection';
import SignalHistorySection from '../components/vsa/SignalHistorySection';
import SignalsTableSection from '../components/vsa/SignalsTableSection';

// ── Compact Watchlist Item ──

// ══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════
export default function VSA() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [regime, setRegime] = useState(null);
  const [signals, setSignals] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [regimeHistory, setRegimeHistory] = useState([]);
  const [setupDist, setSetupDist] = useState([]);
  const [recommendations, setRecommendations] = useState({ signals: [], summary: [], meta: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [selectedDate] = useState(null); // null = latest
  const [signalHistory, setSignalHistory] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState(null); // { text, severity }

  const loadData = useCallback(async (date = null) => {
    setLoading(true);
    setError('');
    try {
      const [summaryRes, regimeRes, latestRes, sectorsRes, setupRes, historyRes, recommendationsRes] = await Promise.all([
        vsaAPI.getSummary(),
        vsaAPI.getMarketRegime(),
        vsaAPI.getLatest(200, null, 0, null, null, date),
        vsaAPI.getSectorsRanking(),
        vsaAPI.getSetupDistribution(),
        vsaAPI.getRegimeHistory(60),
        vsaAPI.getRecommendations().catch(() => ({ data: { signals: [], summary: [], meta: {} } })),
      ]);

      setSummary(summaryRes.data);
      setRegime(regimeRes.data);
      setSignals(latestRes.data || []);
      setSectors(sectorsRes.data.sectors || []);
      setSetupDist(setupRes.data.distribution || []);
      setRegimeHistory(historyRes.data.history || []);
      setRecommendations(recommendationsRes.data || { signals: [], summary: [], meta: {} });

      // Load signal history separately (non-blocking)
      vsaAPI.getSignalHistory(30).then(res => setSignalHistory(res.data)).catch(() => {});
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const runScan = useCallback(async () => {
    setScanning(true);
    setScanMsg(null);
    try {
      const res = await vsaAPI.scanSync(60);
      const n = res.data?.total_signals || 0;
      setScanMsg({ text: t('vsa.actions.scanDone', { count: n }), severity: 'success' });
      await loadData(selectedDate);
    } catch (err) {
      setScanMsg({ text: t('vsa.actions.scanError', { message: err.response?.data?.detail || err.message }), severity: 'error' });
    } finally {
      setScanning(false);
    }
  }, [loadData, selectedDate, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const isFirstLoad = loading && !summary;

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      <OverviewSection
        isFirstLoad={isFirstLoad}
        summary={summary}
        regime={regime}
        signals={signals}
        recommendations={recommendations}
        loading={loading}
        scanning={scanning}
        onRefresh={() => loadData(selectedDate)}
        onScan={runScan}
        sectors={sectors}
        setupDist={setupDist}
        regimeHistory={regimeHistory}
      />

      <RecommendationsSection
        isFirstLoad={isFirstLoad}
        recommendations={recommendations}
        onSelectTicker={setSelectedTicker}
      />

      <SignalHistorySection signalHistory={signalHistory} onSelectTicker={setSelectedTicker} />

      <SignalsTableSection isFirstLoad={isFirstLoad} signals={signals} onSelectTicker={setSelectedTicker} />

      {/* ── Ticker Detail Dialog ──────────────────────────────────── */}
      <TickerDetailDrawer open={!!selectedTicker} onClose={() => setSelectedTicker(null)} ticker={selectedTicker} />

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>

      {/* Scan result Snackbar */}
      <Snackbar open={!!scanMsg} autoHideDuration={4000} onClose={() => setScanMsg(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={scanMsg?.severity || 'success'} onClose={() => setScanMsg(null)} variant="filled" sx={{ fontWeight: 600 }}>
          {scanMsg?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
